import {UserManager, UserManagerSettings, User, UserSettings} from 'oidc-client';
import {AvailableProfileModel, ConfirmDataResponse, SharedResourcesClient, UsersClient} from "@/api-client";
import userInfo from "@/features/shared/components/UserInfo";
import getLandingPageRouteName from "@/components/LandingPageMap";
import toasts from "@/components/Toasts";
import {ls} from "@/CultureService";
import globalLoader from "@/globalLoader";

export let authService: AuthService = null!;

export const profileIdStorageKey = "misProfileId";

export class AuthService {
    availableProfiles: AvailableProfileModel[] = [];
    confirmData: ConfirmDataResponse | null = null;
    handleUserLoaded: Promise<void> = Promise.resolve();
    handleUserUnloaded: Promise<void> = Promise.resolve();

    constructor(private readonly manager: UserManager, private user: User | null) {
        manager.events.addUserSignedOut((): void => {
            this.manager
                .removeUser()
                .then(() => {
                    window.location.href = "/";
                });
        });

        manager.events.addUserUnloaded((): void => {
            // NOTE: OIDC client does not await on this event callbacks.
            this.handleUserUnloaded = this.onUserUnloaded();
        })

        manager.events.addUserLoaded((user: User): void => {
            // NOTE: OIDC client does not await on this event callbacks.
            this.handleUserLoaded = this.onUserLoaded(user);
        })

        this.manager.events.addSilentRenewError((error: Error): void => {
            console.error("Silent renewal of access token failed.")
            console.error(error);
            toasts.warning(ls.AuthService_Toast_Warning_SessionRenewalFailed, 0);
        });

        this.manager.events.addAccessTokenExpired((error: Error): void => {
            console.error("Token expired.")
            this.manager
                .removeUser()
                .then(() => {
                    window.location.href = "/";
                });
        });
    }

    static async initialize(settings: UserManagerSettings, tryToAuthSilently: boolean): Promise<void> {
        const userManager = new UserManager(settings);

        return await userManager
            .getUser()
            .then(async user => {
                authService = new AuthService(userManager, user)

                // Re-initializes everything on full page reload.
                if (authService.isLoggedIn)
                    await authService.onUserLoaded(user!);
                else {
                    const silentlySignedInUser =
                        tryToAuthSilently
                            ? await userManager.signinSilent()
                                .catch(_ => null) // Means that the user is logged out.
                            : null; // To avoid silent sign-in loop.

                    if (silentlySignedInUser)
                        await authService.onUserLoaded(silentlySignedInUser);
                    else
                        await authService.onUserUnloaded();
                }
            });
    }

    // NOTE: Use only in "initialize" & OIDC client event handlers.
    private async onUserLoaded(user: User) {
        globalLoader.startLoading();
        this.user = user;
        await Promise.all([
            this.refreshProfiles(),
            userInfo.loadUserInfo()
        ]);
        globalLoader.endLoading();
    }

    // NOTE: Use only in "initialize" & OIDC client event handlers.
    private async onUserUnloaded() {
        globalLoader.startLoading();
        this.user = null;
        await this.deselectProfile();
        globalLoader.endLoading();
    }

    get isLoggedIn(): boolean {
        return this.user != null && !this.user.expired;
    }

    getAuthorizationHeaderValue(): string {
        if (!this.isLoggedIn)
            throw new Error("Check whether user is logged in before asking authorization header.")

        return `${this.user!.token_type} ${this.user!.access_token}`;
    }

    async authenticateWithAccessLinkToken(token: string, secret: string): Promise<void> {
        return await new SharedResourcesClient().authToken(token, secret).then(async result => {
            const userSettings = {
                // eslint-disable-next-line @typescript-eslint/camelcase
                token_type: result.tokenType,
                // eslint-disable-next-line @typescript-eslint/camelcase
                access_token: result.token,
                // eslint-disable-next-line @typescript-eslint/camelcase
                expires_at: result.expiresAt
            } as UserSettings;

            if (this.isLoggedIn)
                await this.manager.removeUser();

            const user = new User(userSettings);
            await this.manager.storeUser(user);
            await this.onUserLoaded(user);
        });
    }

    startAuthentication(encodedReturnUrl: string): Promise<void> {
        return this.manager
            .signinRedirect({state: {returnUrl: encodedReturnUrl}});
    }

    completeAuthentication(): Promise<string> {
        return this.manager.signinRedirectCallback().then(async user => {
            await this.handleUserLoaded; // NOTE: Waits until "addUserLoaded" has finished. Very important to await here! Failure to do so creates race condition.
            return decodeURI(user.state?.returnUrl || "/");
        });
    }

    async completeSilentRefresh(): Promise<void> {
        await this.manager.signinSilentCallback().then(async user => {
            await this.handleUserLoaded; // NOTE: Waits until "addUserLoaded" has finished. Very important to await here! Failure to do so can create iframe loop.
        });
    }

    hasProfileSelected(): boolean {
        return sessionStorage.getItem(profileIdStorageKey) != null;
    }

    async selectProfile(profile: AvailableProfileModel): Promise<string> {
        if (!this.isLoggedIn)
            throw new Error("Check whether user is logged in before selecting profile.")

        sessionStorage.setItem(profileIdStorageKey, profile.id);
        await Promise.all([
            userInfo.loadUserInfo(),
            this.refreshProfiles()
        ]);
        return getLandingPageRouteName(userInfo.role);
    }

    async deselectProfile() {
        sessionStorage.removeItem(profileIdStorageKey);
        await Promise.all([
            userInfo.loadUserInfo(),
            this.refreshProfiles()
        ]);
    }

    async logout() {
        await this.manager.signoutRedirect();
        await this.handleUserUnloaded; // NOTE: Waits until "addUserUnloaded" has finished.
    }

    async refreshProfiles() {
        if (this.isLoggedIn) {
            this.availableProfiles = await new UsersClient().availableProfiles();
            this.confirmData = await new UsersClient().confirmData();
        } else {
            this.availableProfiles = [];
        }
    }
}